import type { ReactNode } from 'react';
import { cn } from './utils';

export interface SkeletonProps {
  readonly className?: string;
}

/**
 * Esqueleto de carregamento.
 *
 * Preferimos esqueleto a spinner porque preserva o LAYOUT: quem usa leitor de
 * tela ou lupa não perde a posição quando o conteúdo chega, e não há salto de
 * foco. O esqueleto é `aria-hidden` — o estado de carregamento é anunciado uma
 * única vez pelo contêiner (`aria-busy` na tabela, `<LoadingRegion>` nas telas),
 * senão o leitor de tela leria "carregando" dezenas de vezes.
 */
export function Skeleton({ className }: SkeletonProps) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'block rounded bg-surface-sunken',
        'bg-[linear-gradient(90deg,var(--qc-color-surface-sunken)_0%,var(--qc-color-border-subtle)_50%,var(--qc-color-surface-sunken)_100%)]',
        'bg-[length:800px_100%] animate-shimmer',
        className,
      )}
    />
  );
}

/** Bloco de esqueleto para um cartão de métrica. */
export function SkeletonCard() {
  return (
    <div className="rounded-lg border border-border bg-surface p-4">
      <Skeleton className="h-3 w-24" />
      <Skeleton className="mt-3 h-8 w-16" />
      <Skeleton className="mt-3 h-3 w-32" />
    </div>
  );
}

/** Esqueleto de lista com N linhas. */
export function SkeletonList({ rows = 5 }: { readonly rows?: number }) {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} className="h-12 w-full" />
      ))}
    </div>
  );
}

/**
 * Região que anuncia o carregamento UMA vez, envolvendo esqueletos.
 * `aria-live="polite"` + `aria-busy` é o par correto: avisa sem interromper.
 */
export function LoadingRegion({
  loading,
  label = 'Carregando conteúdo…',
  children,
}: {
  readonly loading: boolean;
  readonly label?: string;
  readonly children: ReactNode;
}) {
  return (
    <div aria-busy={loading || undefined} aria-live="polite">
      {loading ? <span className="qc-sr-only">{label}</span> : null}
      {children}
    </div>
  );
}
